/* Demo Queue page — shows items with status "queue".
   Users can complete or delete items. Groups by section when on "all" tab. */

import { useState, useMemo } from "react";
import { ContentTypeTabs } from "@/components/feed/ContentTypeTabs";
import { Tabs } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";
import type { DemoItem } from "./demoData";
import { getSectionForTag } from "./demoData";
import { DemoFeedItemRow } from "./DemoFeedItemRow";


const HIGHLIGHT_CLASS = "ring-2 ring-primary/60 rounded-md transition-all duration-300";

interface DemoQueuePageProps {
  items: DemoItem[];
  onCompleteItem: (id: string) => void;
  onDeleteItem: (id: string) => void;
  highlightedFeature?: string;
}

export function DemoQueuePage({
  items,
  onCompleteItem,
  onDeleteItem,
  highlightedFeature,
}: DemoQueuePageProps) {
  const [activeTab, setActiveTab] = useState("all");
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const queueItems = items.filter((item) => item.status === "queue");

  const availableTags = useMemo(
    () => [...new Set(queueItems.flatMap((item) => item.tags))].sort(),
    [queueItems],
  );

  const filtered = useMemo(() => {
    let result = queueItems;
    if (activeTab !== "all") {
      result = result.filter((item) => item.contentType === activeTab);
    }
    if (filterTag) {
      result = result.filter((item) => item.tags.includes(filterTag));
    }
    return [...result].sort((a, b) =>
      sortOrder === "asc" ? a.addedAt - b.addedAt : b.addedAt - a.addedAt,
    );
  }, [queueItems, activeTab, filterTag, sortOrder]);

  const showSections = activeTab === "all" && !filterTag;

  const groupedItems = useMemo(() => {
    if (!showSections) return filtered;
    return [...filtered].sort((a, b) => {
      const aSection = availableTags.indexOf(a.tags[0]);
      const bSection = availableTags.indexOf(b.tags[0]);
      if (aSection !== bSection) return aSection - bSection;
      return sortOrder === "asc" ? a.addedAt - b.addedAt : b.addedAt - a.addedAt;
    });
  }, [filtered, showSections, sortOrder, availableTags]);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
      <div className="sticky top-0 z-20 bg-background">
        <div className="flex items-center gap-3 px-4 md:px-6 pt-4 pb-2">
          <h1 className="text-lg font-bold text-heading-foreground">Queue</h1>
          <Badge variant="secondary" className="text-xs">
            {queueItems.length}
          </Badge>
        </div>
        <div className={highlightedFeature === "content-filter" || highlightedFeature === "tag-filter" || highlightedFeature === "sort-order" ? HIGHLIGHT_CLASS : ""}>
          <ContentTypeTabs
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            showForYou
            provocativeCount={2}
            filterTag={filterTag}
            onFilterTagChange={setFilterTag}
            availableTags={availableTags}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {groupedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <p className="text-sm">No items in this category</p>
          </div>
        ) : (
          groupedItems.map((item, i) => {
            const sectionName = showSections ? getSectionForTag(item.tags[0]) : null;
            const prevSection = i > 0 && showSections ? getSectionForTag(groupedItems[i - 1].tags[0]) : null;
            const showHeader = sectionName !== null && sectionName !== prevSection;

            const nextSection = i < groupedItems.length - 1 && showSections ? getSectionForTag(groupedItems[i + 1].tags[0]) : null;
            const isLastInSection = i === groupedItems.length - 1 || nextSection !== sectionName;

            return (
              <div key={item.id}>
                {showHeader && (
                  <SectionHeader name={sectionName} highlighted={highlightedFeature === "sections" && prevSection === null} />
                )}
                <DemoFeedItemRow
                  item={item}
                  actions={[
                    {
                      icon: Check,
                      onClick: () => onCompleteItem(item.id),
                      label: "Complete",
                    },
                    {
                      icon: X,
                      onClick: () => onDeleteItem(item.id),
                      label: "Delete",
                    },
                  ]}
                  hideBorder={isLastInSection}
                />
              </div>
            );
          })
        )}
      </div>
    </Tabs>
  );
}

function SectionHeader({ name, highlighted }: { name: string; highlighted?: boolean }) {
  return (
    <div className={`sticky z-10 bg-background flex items-center gap-3 px-4 md:px-6 pt-5 pb-2 ${highlighted ? HIGHLIGHT_CLASS : ""}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-primary whitespace-nowrap">
        {name}
      </span>
      <div className="flex-1 border-t border-border" />
    </div>
  );
}
